/**
 * IconPlaceholder — shadcn v4 icon abstraction for multi-library support.
 * Cogo uses lucide-react exclusively. This component reads the `lucide` prop
 * (icon name) and renders the corresponding lucide icon.
 */
import * as React from "react";
import { icons } from "lucide-react";
import type { LucideProps } from "lucide-react";

interface IconPlaceholderProps extends React.SVGAttributes<SVGSVGElement> {
  /** lucide icon name, e.g. "ChevronDownIcon" or "ChevronDown" */
  lucide?: string;
  /** Ignored — Cogo only supports lucide */
  tabler?: string;
  hugeicons?: string;
  phosphor?: string;
  remixicon?: string;
  "data-slot"?: string;
  "data-icon"?: string;
}

export function IconPlaceholder({
  lucide,
  tabler: _t,
  hugeicons: _h,
  phosphor: _p,
  remixicon: _r,
  className,
  "data-slot": dataSlot,
  "data-icon": dataIcon,
  ...props
}: IconPlaceholderProps) {
  const iconName = lucide ?? dataIcon ?? "";

  // Normalize: "ChevronDownIcon" -> "ChevronDown", "chevron-down" -> "ChevronDown"
  const normalized = iconName
    .replace(/Icon$/, "")
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("") as keyof typeof icons;

  // lucide-react removed the historical Loader2 export in favor of
  // LoaderCircle. Keep shadcn v4 corpus names renderable at this boundary so
  // Spinner and Sonner never degrade into an empty placeholder.
  const normalizedName = normalized as string;
  const compatibleName = (
    normalizedName === "Loader2" ? "LoaderCircle" : normalizedName
  ) as keyof typeof icons;
  const Icon = icons[compatibleName];
  if (!Icon) return null;

  return (
    <Icon
      className={className}
      data-slot={dataSlot}
      {...(props as unknown as LucideProps)}
    />
  );
}
